import {flow, getEnv, types} from "mobx-state-tree";
import {GroupSelectState} from "../../elements/groups/GroupSelectState";
import Notification from "../../../utils/Notification";
import {TagSelectState} from "../../elements/tags/TagSelectState";
import {DimensionsMultiSelectState} from "../../elements/dimensions/multi-select/dimensionsMultiSelectState";
import lodash from "lodash";
import {MediaFile} from "../../../models/mediaFiles/MediaFileModel";
import {CodeTypeSelectState} from "../../elements/codeTypes/CodeTypeSelectState";
import {
  getFileFormat,
  supportedImageFileTypes,
  supportedNonImageFileTypes,
  convertByteToMB,
  isEpsFormat
} from "../../../utils/common"
  import {fileSizeConstants} from "../../../utils/constants"


export const CreateMediaFileState = types.model("CreateMediaFileState", {
  mediaFile: types.maybe(MediaFile),
  isLoading: types.optional(types.boolean, false),
  isMadeDefault: types.optional(types.boolean, false),
  groupSelectStore: types.maybe(types.reference(GroupSelectState)),
  tagSelectStore: types.maybe(types.late(() => types.reference(TagSelectState))),
  isExclusivelyGroupOwned: types.optional(types.boolean, true),
  dimensionsMultiSelectStore: types.maybeNull(types.late(() => types.reference(DimensionsMultiSelectState))),
  dimensionAll: types.optional(types.boolean, true),
  isDimensionSelectVisible: types.optional(types.boolean, false),
  fileIsCorrectSize: types.optional(types.boolean, true),
  fileIsCorrectType: types.optional(types.boolean, true),
  fileSize: types.optional(types.number, 0),
  fileFormat: types.optional(types.string, ''),
  base64FileImage: types.optional(types.string, ''),
  fileForS3Upload: types.optional(types.frozen(),{}),
  base64NonBrowserConvertedFile: types.optional(types.string, ''),
  pdfLogoPng: types.optional(types.string, ''),
  codeTypeSelectStore: types.maybeNull(types.reference(CodeTypeSelectState)),

})
  .actions(self => ({
    handleIsDefault() {
      self.isMadeDefault = !self.isMadeDefault
    },
    validateForm() {
      let isValid = true;
      isValid = self.mediaFile.validateMediaFileModel() // validate fields other than name
      return isValid;
    },
    toggleIsExclusivelyGroupOwned() {
      self.isExclusivelyGroupOwned = !self.isExclusivelyGroupOwned
    },
    toggleDimensionAll() {
      self.dimensionAll = !self.dimensionAll
      self.toggleIsDimensionSelectVisible(!self.dimensionAll)
      self.dimensionAll ? self.dimensionsMultiSelectStore.selectAllDimensions() : self.dimensionsMultiSelectStore.reset()
    },
    toggleIsDimensionSelectVisible(visible) {
      self.isDimensionSelectVisible = visible
    },
    hydrateDimension(dimensions = []) {
      const isDimensionAll = Object.values(dimensions).every(item => lodash.isEmpty(item));
      self.dimensionAll = !isDimensionAll
      self.toggleDimensionAll()
      if (!self.dimensionAll) {
        self.dimensionsMultiSelectStore.hydrateDimensions(dimensions)
      }
    },
    setBase64Image(base64) {
      self.base64FileImage = base64
    },
    setBase64NonBrowserConvertedFile(str) {
      self.base64NonBrowserConvertedFile = str
    },
    setFileIsCorrectSize(bool) {
      self.fileIsCorrectSize = bool
    },
    setFileIsCorrectType(bool) {
      self.fileIsCorrectType = bool
    },
    setFileSize(size) {
      self.fileSize = size
    },
    removeFile() {
      self.base64FileImage = ""
    },

    setFileForS3Upload(file) {
      self.fileForS3Upload = file
    },
    setPdfLogoPng(pngBase64) {
      self.pdfLogoPng = pngBase64
      // self.notifyDesigner("pdfLogoPng",pngBase64)
    },
    setUsage(usage) {

      if (self.fileForS3Upload instanceof File) {
        let errorMessages = [];

        if (!supportedImageFileTypes(self.mediaFile?.fileFormat)) {
          errorMessages.push(`File format: ${self.mediaFile?.fileFormat} is not supported for Qr Image.`);
        }
        if (usage === "qrImage" && self.fileSize > fileSizeConstants.MAX_UPLOAD_QR_IMAGE) {
          errorMessages.push(`File size: ${convertByteToMB(self.fileSize )} MB is larger than Qr Image supports.`);
        }
        if (errorMessages.length > 0) {
          new Notification()
            .setType("error")
            .setMessage(errorMessages.join("\n")) // Double line break for better readability
            .send();
          return;
        }
      }
      self.mediaFile.updateMediaFile({"usage": usage})
    },
    async setSelectedImage(file, isLogo, eyesAndModulesStore) {
      let processedFile = file;
      self.setBase64NonBrowserConvertedFile("")
      const manager = getEnv(self).mediaFileManager;

      // Test EPS conversion if it's an EPS file
      if (isEpsFormat(file)) {
        try {

          const base64SrcImg = await manager.convertEpsToBase64(file);
          self.setBase64NonBrowserConvertedFile(base64SrcImg) // Set for preview
          processedFile = file; // Keep original EPS for upload
        } catch (error) {
          new Notification()
            .setType("error")
            .setMessage(`Failed to handle EPS file: ${error.message}`)
            .send();
          return;
        }
      }

      const fileFormat = getFileFormat(processedFile)
      self.setFileIsCorrectType(supportedImageFileTypes(fileFormat) || supportedNonImageFileTypes(fileFormat))

      if (!self.fileIsCorrectType || processedFile?.size > fileSizeConstants.MAX_UPLOAD_CONTENT_FILE) {
        if(!self.fileIsCorrectType){
        new Notification()
          .setType("error")
          .setMessage(`File type: ${fileFormat} is not accepted`)
          .send();
        }
        if(processedFile?.size > fileSizeConstants.MAX_UPLOAD_CONTENT_FILE){
          new Notification()
            .setType("error")
            .setMessage(`File size: ${convertByteToMB(processedFile?.size)} MB is larger than both content and qr image supports.`)
            .send();
        }
          return;
      }
      if(self.mediaFile.usage === "qrImage" && !supportedImageFileTypes(fileFormat)){
          self.mediaFile.updateMediaFile({"usage": "content"})
        new Notification()
          .setType("warning")
          .setMessage(`File format: ${fileFormat} is not supported for Qr Image`)
          .send();
      }
      if(self.mediaFile.usage === "qrImage" && processedFile?.size > fileSizeConstants.MAX_UPLOAD_QR_IMAGE){
          self.mediaFile.updateMediaFile({"usage": "content"})
        new Notification()
          .setType("warning")
          .setMessage(`File size: ${convertByteToMB(file?.size)} MB is larger than Qr Image supports.`)
          .send();
      }
      self.setFileIsCorrectSize(processedFile?.size < fileSizeConstants.MAX_UPLOAD_CONTENT_FILE)


      if (self.fileIsCorrectSize && self.fileIsCorrectType) {
        self.setFileForS3Upload(processedFile)
        self.mediaFile.setFileFormat(fileFormat)
        self.setFileSize(processedFile?.size)

        let reader = new FileReader() // show image preview before upload
        reader.onload = function (event) {
          if (fileFormat === "svg+xml" || fileFormat === "svg") {
            // Keep full data URI for SVG
            self.setBase64Image(reader.result);
          } else {
            // Only keep base64 string for other formats
            self.setBase64Image(reader.result.split("base64,")[1])
          }
        }.bind(this)

        if (processedFile) {
          // reader.readAsArrayBuffer(processedFile)
          reader.readAsDataURL(processedFile);
        }

        // self.notifyPreview()
      } else {
        !self.fileIsCorrectSize && new Notification()
          .setType("error")
          .setMessage(`File size: ${convertByteToMB(processedFile?.size)} MB is larger than 200 MB upload limit.`)
          .send();
        !self.fileIsCorrectType && new Notification()
          .setType("error")
          .setMessage(`File type: ${fileFormat} is not accepted \n   `)
          .send();
      }
    },
    createNewLocationUploadFile: flow(function* f() {
      self.isLoading = true;

      const manager = getEnv(self).mediaFileManager;
      try {
        const resp = yield manager.createNewLocationUploadFile(self);
        if (resp) {
          new Notification()
            .setType("success")
            .setMessage(`Media File created`)
            .send();
          return resp;
        } else {
          new Notification()
            .setType("error")
            .setMessage(`Media File failed creating`)
            .send();
        }
      } catch (e) {

        if (e.response?.status === 415) {
          new Notification(
            "error",
            "The uploaded file format is not supported. Please upload a file with a valid format."
          )
            .setTitle("Unsupported File Format")
            .send();
        } else if (e.response?.status === 403) {
          new Notification(
            "error",
            "Your permission set does not allow you to access that feature and/or functionality. Please contact an administrator to modify your permission set if needed."
          )
            .setTitle("Missing Permission")
            .send();
        } else {
          new Notification()
            .setType("error")
            .setMessage(`${e.message}`)
            .send();
        }
        console.error("Error in createNewLocationUploadFile:", e);
      } finally {
        self.isLoading = false;
      }
    }),

    selectExperience(experience){
      self.selectedExperience = experience
    }

  })).views((self) => ({
    usageIsQRImage() {
      return self.mediaFile.usage === "qrImage"
    },
    usageIsContent() {
      return self.mediaFile.usage === "content"
    },
    shouldRenderFileImage() {
     // const isEps =  manager.isEpsFile(self.fileForS3Upload)
      return self.mediaFile.fileFormat !== "" && supportedImageFileTypes(self.mediaFile.fileFormat) &&
        !isEpsFormat(self.fileForS3Upload)
    },
    imagePreviewSrc(){
      if (self.mediaFile.fileFormat === "svg" || self.mediaFile.fileFormat === "svg+xml") {
        return self.base64FileImage;
      }
      return `data:${self.mediaFile.fileFormat};base64,${self.base64FileImage}`;
    },
    shouldRenderFileIcon() {
      return  (self.mediaFile?.fileFormat || "") !== "" &&
        supportedNonImageFileTypes(self.mediaFile?.fileFormat || "") &&
        self.fileForS3Upload?.type !== "application/pdf"
    },
    shouldRenderStagedPDFToUpload(){
      return self.fileForS3Upload?.type === "application/pdf"
    },
    getExperienceOptions() {
      const options = self.experienceOptions || {};
      return options || []
    },

  }))

